import {emotionStyled, createVars} from '@packages/utilities/styled';
import {Text} from '../Text';
import {Link} from '../Link';
import {Button} from '../Button';
import {CloseButton} from '../CloseButton';
import {tokens} from '../../themes';
import {activeSelector, hoverSelector} from '../../utils';
import {HStack} from '../Stack';

export const [cssVars, restyleVars] = createVars(
  {
    mainColor: tokens.colors.textPrimary,
    linkColor: tokens.colors.blueText,
    tintedBackground: tokens.colors.background2,
    iconColor: tokens.colors.textPrimary,
    textColor: tokens.colors.textSecondary,
    titleColor: tokens.colors.textPrimary,
    closeButtonFocusOutlineColor: tokens.colors.actionPrimaryBackground,
    closeButtonHoverBackground: tokens.colors.closeButtonBackground,
    closeButtonSvgColor: tokens.colors.textPrimary,
    closeButtonSvgHoverColor: tokens.colors.textPrimary,
  },
  {prefix: 'note'}
);

/**
 * This component is intentionally unstyled.
 * We're using NoteTitleView as a selector within the isInline variant of NoteViewD2.
 */
export const NoteTitleView = emotionStyled(Text)(
  {color: cssVars.titleColor},
  {componentName: 'NoteTitleView'}
);

export const CloseButtonView = emotionStyled(CloseButton)({
  cursor: tokens.ui.activeCursor,
  [`${hoverSelector}, ${activeSelector}`]: {
    backgroundColor: cssVars.closeButtonHoverBackground,
  },
  '&:focus-visible': {
    outline: `1px solid ${cssVars.closeButtonFocusOutlineColor}`,
    outlineOffset: '1px',
  },
  svg: {
    color: cssVars.closeButtonSvgColor,
    '&:hover': {
      color: cssVars.closeButtonSvgHoverColor,
    },
  },
});

export const IconWrapperView = emotionStyled('div')({
  color: cssVars.iconColor,
});

export const NoteButtonView = emotionStyled(Button)(
  {},
  {
    componentName: 'NoteButton',
  }
);

export const NoteActionsView = emotionStyled(HStack)(
  {},
  {
    componentName: 'NoteActions',
    variants: {
      isInline: {
        true: {
          justifyContent: 'flex-end',
          width: 'fit-content',
        },
      },
    },
  }
);

export const NoteBodyView = emotionStyled(Text)(
  {
    color: cssVars.textColor,
  },
  {
    componentName: 'NoteBody',
  }
);

export const NoteView = emotionStyled('div')(
  {
    background: '$uiCardBackground',
    borderRadius: '$2',
    color: cssVars.textColor,
    fontSize: '$body',
    lineHeight: '$body',
    padding: '$2',
    [`& [data-sc="link"]:not([type="button"])`]: {
      color: cssVars.textColor,
      textDecoration: 'underline',
    },
  },
  {
    componentName: 'Note',
    variants: {
      colorVariant: {
        default: {
          ...restyleVars({}),
        },
        primary: {
          ...restyleVars({
            tintedBackground: tokens.colors.blueTransparent,
            iconColor: tokens.colors.blueIcon,
          }),
        },
        success: {
          ...restyleVars({
            tintedBackground: tokens.colors.greenTransparent,
            iconColor: tokens.colors.greenIcon,
          }),
        },
        upgrade: {
          ...restyleVars({
            tintedBackground: tokens.colors.greenTransparent,
            iconColor: tokens.colors.greenIcon,
          }),
        },
        danger: {
          ...restyleVars({
            tintedBackground: tokens.colors.redTransparent,
            iconColor: tokens.colors.redIcon,
          }),
        },
        urgent: {
          ...restyleVars({
            tintedBackground: tokens.colors.orangeTransparent,
            iconColor: tokens.colors.orangeIcon,
          }),
        },
        warning: {
          ...restyleVars({
            tintedBackground: tokens.colors.yellowTransparent,
            iconColor: tokens.colors.yellowIcon,
          }),
        },
      },
      size: {
        medium: {},
        'x-large': {
          padding: '$3',
        },
      },
      isInline: {
        true: {
          [`&:has(${Button})&:not(:has(${NoteTitleView}))`]: {
            [`${IconWrapperView}`]: {
              padding: tokens.ui.button.padding.comfortable,
              paddingLeft: 0,
              paddingRight: 0,
            },
            [`${NoteBodyView}`]: {
              padding: tokens.ui.button.padding.comfortable,
              paddingLeft: 0,
              paddingRight: 0,
            },
            [`${CloseButtonView}`]: {
              margin: tokens.ui.button.padding.comfortable,
              marginLeft: 0,
              marginRight: 0,
            },
          },
        },
      },
      isNested: {
        true: {
          background: 'transparent',
          /* Hard-coded for visual alignment for most cases. */
          padding: '6px $1',
        },
      },
      isSeamless: {
        true: {
          padding: 0,
        },
      },
      isTinted: {
        true: {
          background: cssVars.tintedBackground,
          ...restyleVars(
            {textColor: tokens.colors.textPrimary},
            {setAll: false}
          ),
        },
      },
      isGhost: {
        true: {
          background: 'transparent',
        },
      },
      isToast: {
        true: {},
      },
      hasTitle: {
        true: {} /* used for compound variant */,
      },
    },
    defaultVariants: {
      colorVariant: 'default',
    },
    compoundVariants: [
      {
        conditions: {hasTitle: true, colorVariant: 'default'},
        css: {
          ...restyleVars(
            {iconColor: tokens.colors.textPrimary},
            {setAll: false}
          ),
        },
      },
      {
        conditions: {isTinted: true, colorVariant: 'primary'},
        css: {
          ...restyleVars({linkColor: tokens.colors.blueText}, {setAll: false}),
        },
      },
      {
        conditions: {isTinted: true, colorVariant: 'success'},
        css: {
          ...restyleVars({linkColor: tokens.colors.greenText}, {setAll: false}),
        },
      },
      {
        conditions: {isTinted: true, colorVariant: 'upgrade'},
        css: {
          ...restyleVars({linkColor: tokens.colors.greenText}, {setAll: false}),
        },
      },
      {
        conditions: {isTinted: true, colorVariant: 'danger'},
        css: {
          ...restyleVars({linkColor: tokens.colors.redText}, {setAll: false}),
        },
      },
      {
        conditions: {isTinted: true, colorVariant: 'urgent'},
        css: {
          ...restyleVars(
            {linkColor: tokens.colors.orangeText},
            {setAll: false}
          ),
        },
      },
      {
        conditions: {isTinted: true, colorVariant: 'warning'},
        css: {
          ...restyleVars(
            {linkColor: tokens.colors.yellowText},
            {setAll: false}
          ),
        },
      },
    ],
  }
);

export const NoteLinkView = emotionStyled(Link)(
  {
    color: cssVars.linkColor,
    [`${NoteBodyView} &`]: {
      color: cssVars.textColor,
      textDecoration: 'underline',
    },
  },
  {
    componentName: 'NoteLink',
  }
);
